import React, { useState } from "react";
import {
  Box, Container, Typography, Grid, IconButton, Chip, Divider,
  Dialog, DialogTitle, DialogContent,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "../../../context/ThemeContext";
import Logo from "../../../assets/LogoNew2.png";

const Footer = () => {
  const { theme } = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");

  const companyLinks = ["About Us", "How It Works", "Features", "Contact", "FAQs"];
  const companySections = ["about-us", "how-it-works", "features", "about-us", "faqs"];
  const userLinks = ["Property Owners", "Tenants", "List Property", "Search Properties"];

  const handleOpen = (type) => { setModalType(type); setOpenModal(true); };
  const handleClose = () => setOpenModal(false);
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const contactItems = [
    { icon: <EmailIcon sx={{ fontSize: 16 }} />, text: "ownertenantsolution@gmail.com", color: "#4facfe" },
    { icon: <PhoneIcon sx={{ fontSize: 16 }} />, text: "+92 349 2565577", color: "#43e97b" },
    { icon: <LocationOnIcon sx={{ fontSize: 16 }} />, text: "House #B-38, Street #03, Bhittaiabad, Gulistan-e-Jauhar, Block 9, Karachi, Pakistan", color: "#f093fb" },
  ];

  const socialItems = [
    { icon: <FacebookIcon fontSize="small" />, hover: "#1877f2" },
    { icon: <InstagramIcon fontSize="small" />, hover: "#e4405f" },
    { icon: <WhatsAppIcon fontSize="small" />, hover: "#25d366" },
    { icon: <LinkedInIcon fontSize="small" />, hover: "#0077b5" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", type: "privacy" },
    { label: "Terms of Service", type: "terms" },
    { label: "Refund Policy", type: "refund" },
    { label: "Merchant Disclosure", type: "merchant" },
  ];

  return (
    <Box sx={{ bgcolor: "#08080f", pt: 10, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Top gradient accent */}
        <Box
          sx={{
            height: 2,
            background: `linear-gradient(90deg, transparent, ${theme.primary}, ${theme.secondary || "#667eea"}, ${theme.primary}, transparent)`,
            borderRadius: 99,
            mb: 8,
          }}
        />

        <Grid container spacing={5}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1.5 }}>
              <Box
                sx={{
                  height: 44, width: 44, flexShrink: 0,
                  display: "inline-block",
                  backgroundColor: theme.primary,
                  maskImage: `url(${Logo})`,
                  WebkitMaskImage: `url(${Logo})`,
                  maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat",
                  maskSize: "120%", WebkitMaskSize: "120%",
                  maskPosition: "center", WebkitMaskPosition: "center",
                }}
              />
              <Box>
                <Typography sx={{ color: "white", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>
                  Owner Tenant Solution
                </Typography>
                <Typography sx={{ color: theme.primary, fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.2em" }}>
                  OTS
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.45)", mb: 4, lineHeight: 1.9, fontSize: "0.82rem" }}>
              Pakistan's premier rental management platform — connecting property owners
              with verified tenants through a secure, transparent digital ecosystem.
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 4 }}>
              {socialItems.map((s, i) => (
                <IconButton key={i} size="small" sx={{
                  color: "rgba(255,255,255,0.5)",
                  bgcolor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  transition: "all 0.25s",
                  "&:hover": { bgcolor: s.hover, color: "white", borderColor: s.hover, transform: "translateY(-3px)" },
                }}>
                  {s.icon}
                </IconButton>
              ))}
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", display: "block", mb: 1 }}>
                Secure Payments By
              </Typography>
              <Chip
                icon={<AccountBalanceIcon sx={{ color: "#4caf50 !important", fontSize: "15px !important" }} />}
                label="SafePay"
                size="small"
                sx={{
                  bgcolor: "rgba(76,175,80,0.08)",
                  color: "#4caf50",
                  border: "1px solid rgba(76,175,80,0.25)",
                  fontWeight: 700, fontSize: "0.73rem",
                }}
              />
            </Box>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} md={2}>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.12em", display: "block", mb: 2.5, fontSize: "0.68rem" }}>
              Company
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
              {companyLinks.map((link, i) => (
                <Typography key={link} onClick={() => scrollToSection(companySections[i])} variant="body2"
                  sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: "0.84rem", transition: "all 0.2s",
                    "&:hover": { color: theme.primary, pl: "6px" } }}>
                  {link}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* User Links */}
          <Grid item xs={6} md={2}>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.12em", display: "block", mb: 2.5, fontSize: "0.68rem" }}>
              For Users
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
              {userLinks.map((link) => (
                <Typography key={link} variant="body2"
                  sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: "0.84rem", transition: "all 0.2s",
                    "&:hover": { color: theme.primary, pl: "6px" } }}>
                  {link}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={4}>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.12em", display: "block", mb: 2.5, fontSize: "0.68rem" }}>
              Contact Us
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {contactItems.map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box sx={{
                    mt: 0.2, flexShrink: 0, color: item.color,
                    bgcolor: `${item.color}12`, p: 0.75, borderRadius: 1.5, display: "flex",
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", lineHeight: 1.7, fontSize: "0.83rem" }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,0.06)" }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.77rem" }}>
            © {new Date().getFullYear()} Owner Tenant Solution (OTS). All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, flexWrap: "wrap" }}>
            {legalLinks.map((item) => (
              <Typography key={item.type} onClick={() => handleOpen(item.type)} variant="body2"
                sx={{ color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: "0.77rem", transition: "color 0.2s",
                  "&:hover": { color: theme.primary } }}>
                {item.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Modal */}
      <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "78vh" } }}>
        <DialogTitle sx={{
          position: "sticky", top: 0, bgcolor: "white", zIndex: 10,
          borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between",
          alignItems: "center", fontWeight: 700, py: 2, px: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 4, height: 22, bgcolor: theme.primary, borderRadius: 99 }} />
            {modalType === "privacy" && "Privacy Policy"}
            {modalType === "terms" && "Terms & Conditions"}
            {modalType === "refund" && "Refund & Cancellation Policy"}
            {modalType === "merchant" && "Merchant Disclosure"}
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#ebebeb" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3.5 }}>
          {modalType === "privacy" && (
            <Box sx={{ "& p": { color: "#555", lineHeight: 1.9, mb: 2, fontSize: "0.92rem" } }}>
              <Typography paragraph>OwnerTenantSolution collects user information such as full name, phone number, email, and address solely for service delivery.</Typography>
              <Typography paragraph>All payments are processed via secure encrypted gateways. We do not store card or banking credentials.</Typography>
              <Typography paragraph>User data is used only for booking coordination and account management. Data is never sold to third parties.</Typography>
              <Typography paragraph>Users may request data deletion by emailing: <strong>ownertenantsolution@gmail.com</strong></Typography>
            </Box>
          )}
          {modalType === "terms" && (
            <Box sx={{ "& p": { color: "#555", lineHeight: 1.9, mb: 1.5, fontSize: "0.92rem" } }}>
              <Typography paragraph>• Property owners are responsible for listing accuracy.</Typography>
              <Typography paragraph>• Tenants must provide truthful information.</Typography>
              <Typography paragraph>• OwnerTenantSolution acts solely as an intermediary.</Typography>
              <Typography paragraph>• Fraudulent listings or fake bookings may result in suspension.</Typography>
            </Box>
          )}
          {modalType === "refund" && (
            <Box sx={{ "& p": { color: "#555", lineHeight: 1.9, mb: 1.5, fontSize: "0.92rem" } }}>
              <Typography paragraph>Booking amounts are held securely in the platform wallet.</Typography>
              <Typography paragraph>• Owner cancels before confirmation → <strong>Full refund</strong></Typography>
              <Typography paragraph>• Tenant cancels before confirmation → <strong>Full refund</strong></Typography>
              <Typography paragraph>• Tenant cancels after owner confirmation → <strong>No refund</strong></Typography>
              <Typography paragraph>• Refund Timeline: <strong>3–7 business days</strong></Typography>
            </Box>
          )}
          {modalType === "merchant" && (
            <Box sx={{ "& p": { color: "#555", lineHeight: 1.9, mb: 1.5, fontSize: "0.92rem" } }}>
              <Typography paragraph><strong>Legal Business Name:</strong> OwnerTenantSolution (Owner Tenant Solution)</Typography>
              <Typography paragraph><strong>Business Type:</strong> Online Property Rental Management Platform</Typography>
              {/* ✅ MCC ADDED — was missing before */}
              
              <Typography paragraph><strong>Pricing (Owners):</strong> PKR 500/month per property listed</Typography>
              <Typography paragraph><strong>Commission (Tenants):</strong> 5% on confirmed booking amount</Typography>
              <Typography paragraph><strong>Refund Timeline:</strong> 3–7 business days</Typography>
              <Typography paragraph><strong>Contact:</strong> +92 349 2565577 | ownertenantsolution@gmail.com</Typography>
              <Typography paragraph>Only lawful property listings are permitted. Illegal, fraudulent, or misleading content is strictly prohibited.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Footer;